import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap, tap } from 'rxjs';
import { ProfilePictureCacheService } from 'src/core/services/profile-picture/profile-picture-cache.service';

/** Ratio between the avatar diameter and the font size of its initials. */
const INITIALS_FONT_RATIO = 0.36;

/**
 * Ratio between the avatar diameter and the size of the fallback icon. The icon is a glyph, so it would
 * otherwise inherit the host's initials font size and come out far smaller than the circle around it.
 */
const ICON_FONT_RATIO = 0.55;

/**
 * Circular user avatar: shows the ABP profile picture when the user has one, and falls back to
 * the initials of the given name (or a generic user icon) when they do not.
 *
 * Colors can be overridden by the host through the `--cx-user-avatar-bg` and
 * `--cx-user-avatar-color` custom properties.
 */
@Component({
  selector: 'cx-user-avatar',
  standalone: true,
  templateUrl: './cx-user-avatar.component.html',
  styleUrl: './cx-user-avatar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class:
      'cx-user-avatar d-inline-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden rounded-circle fw-semibold lh-1 user-select-none',
    '[style.width.px]': 'size()',
    '[style.height.px]': 'size()',
    '[style.font-size.px]': 'initialsFontSize()',
  },
})
export class CxUserAvatarComponent {
  private readonly profilePictures = inject(ProfilePictureCacheService);

  /** Identity user id the profile picture belongs to. */
  readonly userId = input<string | null>(null);
  /** Full name of the user; drives the initials fallback and the image alt text. */
  readonly name = input<string | null>(null);
  /** Rendered diameter, in pixels. */
  readonly size = input(32);
  /** What is rendered while the user has no profile picture. */
  readonly fallback = input<'initials' | 'icon'>('initials');

  private readonly imageBroken = signal(false);

  private readonly picture = toSignal(
    toObservable(this.userId).pipe(
      switchMap(userId => this.profilePictures.watchPicture(userId)),
      // A picture that arrives after an invalidation deserves another chance at being decoded.
      tap(() => this.imageBroken.set(false)),
    ),
    { initialValue: null },
  );

  readonly imageSource = computed(() => (this.imageBroken() ? null : this.picture()));

  readonly initials = computed(() => {
    const parts = (this.name() ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return '?';
    }

    const letters =
      parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0];

    return letters.toLocaleUpperCase();
  });

  readonly initialsFontSize = computed(() => Math.round(this.size() * INITIALS_FONT_RATIO));

  readonly iconFontSize = computed(() => Math.round(this.size() * ICON_FONT_RATIO));

  /** A stored picture that cannot be decoded should not leave an empty circle behind. */
  onImageError(): void {
    this.imageBroken.set(true);
  }
}
